'use client';

import { AnalyticsEvent } from '@/shared/lib/analytics/events';
import posthog from 'posthog-js';
import { useCallback, useState } from 'react';

export interface PeekVideoRef {
  thumbnailSrc: string;
  title: string;
}

export type SubscriptionModalContext =
  | { type: 'like'; videoTitle: string; anchorEl?: HTMLElement | null }
  | { type: 'save'; videoTitle: string; anchorEl?: HTMLElement | null }
  | { type: 'comment'; videoTitle: string; anchorEl?: HTMLElement | null }
  | { type: 'clip'; videoTitle: string; anchorEl?: HTMLElement | null }
  | {
      type: 'locked-tab';
      tabName: string;
      creatorName: string;
      creatorAvatarSrc?: string;
      anchorEl?: HTMLElement | null;
      peekVideos?: ReadonlyArray<PeekVideoRef>;
      totalLockedCount?: number;
    }
  | { type: 'locked-video'; videoTitle: string };

/**
 * Hook for gating guest interactions behind a subscription modal.
 *
 * The caller owns the guest decision and only calls `gate()` from its guest
 * branch. The hook must not re-check: a second definition of "guest" turns
 * every disagreement into a click that does nothing (FRONT-554 — creator tabs
 * treat logged-in-but-unsubscribed users as guests, a `!user` check did not).
 *
 * Usage:
 *   const { gate, modalContext, closeModal } = useGuestGate();
 *   <button onClick={() => gate({ type: 'like', videoTitle })}>Like</button>
 */
export function useGuestGate() {
  const [modalContext, setModalContext] = useState<SubscriptionModalContext | null>(null);

  const gate = useCallback((context: SubscriptionModalContext): void => {
    setModalContext(context);
    posthog.capture(AnalyticsEvent.FREEMIUM_GATE_TRIGGERED, {
      gate_type: context.type,
    });
  }, []);

  const closeModal = useCallback(() => setModalContext(null), []);

  return { gate, modalContext, closeModal };
}
